/**
 * Who is working — local-first, and the record Phase 6 adopts rather than replaces.
 *
 * No account, no network, no first-paint cost. The name and the language live in localStorage
 * because the chrome bar draws them before anything async has settled, and a profile that
 * flashes "Unnamed" for 200ms every load is worse than no profile. Only the picture is heavy
 * enough to need IndexedDB, and only the picture is loaded lazily.
 *
 * ── The database, deliberately its own ────────────────────────────────────────────────────
 * The avatar does NOT live in `storage.ts`'s `euclid` database. That database's version is
 * shared, and a store added there has to be added inside an upgrade every other feature also
 * edits — the classic way to end up with a `VersionError` on someone else's machine and a
 * feature that silently never opens. `euclid-profile` is opened at version 1 by this file
 * alone, so its upgrade path cannot collide with anything. When the two are worth merging,
 * this is one `getAll` and one `putMany`.
 */
import { computed, onScopeDispose, ref, watch } from 'vue';
import { activeAccount, scopedName } from './workspaces';

import type { Locale } from './curriculum';

export interface Profile {
  displayName: string;
  /**
   * One line about yourself, and deliberately not a bio.
   *
   * Capped at `DESCRIPTION_MAX` in the store rather than only in the field, because the field is
   * not the only writer — a record restored from an older localStorage entry, or one edited by
   * hand, would otherwise put a paragraph on a page whose whole point is that it has none.
   */
  description: string;
  locale: Locale;
  /**
   * When the stored picture was last written; `0` means there is none.
   *
   * A timestamp rather than a boolean because it is also the cache key: the object URL is
   * rebuilt exactly when this changes, so replacing the picture cannot leave the old one on
   * screen.
   */
  avatarAt: number;
  createdAt: number;
}

/*
 * The base key. The one actually read and written is this scoped to the signed-in account, by the
 * same rule `lib/workspaces.ts` applies to the databases — a name and a description belong to a
 * person, and seeing the previous account's name after signing in as somebody else is
 * indistinguishable from not having switched at all.
 */
const KEY = 'euclid.profile';

const keyNow = (): string => scopedName(KEY, activeAccount());

/** A line, not a paragraph. Eighty characters is about as much as fits under a name. */
export const DESCRIPTION_MAX = 80;

const DEFAULTS: Profile = {
  displayName: '',
  description: '',
  locale: 'mixed',
  avatarAt: 0,
  createdAt: 0,
};

const read = (): Profile => {
  try {
    const stored = localStorage.getItem(keyNow());
    if (!stored) return { ...DEFAULTS, createdAt: Date.now() };
    return { ...DEFAULTS, ...(JSON.parse(stored) as Partial<Profile>) };
  } catch {
    return { ...DEFAULTS, createdAt: Date.now() };
  }
};

const state = ref<Profile>(read());

export const profile = computed<Profile>(() => state.value);

function write(next: Profile): void {
  state.value = next;
  try { localStorage.setItem(keyNow(), JSON.stringify(next)); } catch { /* private window */ }
}

export function updateProfile(patch: Partial<Profile>): void {
  write({ ...state.value, ...patch });
}

export const setDisplayName = (name: string): void => updateProfile({ displayName: name.trim() });

/** Collapses whitespace as well as trimming: a line about yourself has no line breaks in it. */
export const setDescription = (text: string): void =>
  updateProfile({ description: text.replace(/\s+/g, ' ').trim().slice(0, DESCRIPTION_MAX) });

export const setLocale = (locale: Locale): void => updateProfile({ locale });

/**
 * Two letters for the fallback mark.
 *
 * Falls back to the Paint 6000 E rather than to a person glyph: an empty circle with a generic
 * silhouette in it says "no user", and there is always exactly one user here.
 */
export const initials = computed(() => {
  const parts = state.value.displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'E';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
});

// ── The picture ────────────────────────────────────────────────────────────────────────────

/*
 * The base name. The database actually opened is this scoped to whoever is signed in — a display
 * name and a portrait belong to a person, and showing the previous account's face after signing
 * in as somebody else is indistinguishable from not having switched at all.
 */
const DB_NAME = 'euclid-profile';
const DB_VERSION = 1;
const STORE = 'avatar';
const ROW = 'me';

/** The longest side of a stored picture. It is displayed at 96px; 256 covers 2× and a retina 2×. */
export const AVATAR_MAX = 256;

let handle: Promise<IDBDatabase> | null = null;

/**
 * Forget the open database, so the next read opens the one the current account owns.
 *
 * Called when the signed-in account changes. The handle is closed rather than dropped: an open
 * connection blocks a version change on that name, and on WebKit blocks writes outright.
 */
export async function reopenProfile(): Promise<void> {
  const previous = handle;
  handle = null;
  try { (await previous)?.close(); } catch { /* a failed open has nothing to close */ }
  // And the record in front of the reader, which is the half somebody actually notices.
  state.value = read();
}

function openProfileDb(): Promise<IDBDatabase> {
  if (handle) return handle;
  const name = scopedName(DB_NAME, activeAccount());
  handle = new Promise((resolve, reject) => {
    const request = indexedDB.open(name, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    // Every one of the three, or a blocked open hangs a promise that nothing ever settles.
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('profile database is blocked by another tab'));
  });
  return handle;
}

const runOn = async <T,>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openProfileDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = work(tx.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * The box a picture is scaled into: never enlarged, aspect preserved.
 *
 * Cropping to a square here would be irreversible, and the frame already crops with
 * `object-fit: cover` — so the stored picture keeps its shape and the display decides.
 */
export function fitWithin(width: number, height: number, max: number): { w: number; h: number } {
  const longest = Math.max(width, height);
  if (longest <= max || longest === 0) return { w: Math.max(1, width), h: Math.max(1, height) };
  const scale = max / longest;
  return { w: Math.max(1, Math.round(width * scale)), h: Math.max(1, Math.round(height * scale)) };
}

/**
 * Down to 256px before it is stored.
 *
 * A phone photo is 12 megapixels and about 4MB. Keeping that for a 96px frame costs the origin's
 * quota, costs a decode on every open, and buys nothing a 40KB webp does not.
 */
async function downscale(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const { w, h } = fitWithin(bitmap.width, bitmap.height, AVATAR_MAX);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('no 2d context');
    context.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/webp', 0.86));
    if (blob) return blob;
    const png = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
    if (!png) throw new Error('the canvas produced no image');
    return png;
  } finally {
    bitmap.close();
  }
}

/** What the store holds. `blob` is the old shape and is still read — see `setAvatar`. */
interface AvatarRow {
  id: string;
  at: number;
  bytes?: ArrayBuffer;
  type?: string;
  blob?: Blob;
}

/**
 * Store a picture the owner picked off his device.
 *
 * Throws when the file is not something the browser can decode — HEIC straight off an iPhone is
 * the case that actually happens — and the caller says so rather than failing silently.
 *
 * ── Bytes, not the Blob ────────────────────────────────────────────────────────────────────
 * The row holds an ArrayBuffer and the type beside it rather than the Blob itself. WebKit
 * refuses to structured-clone a canvas-produced Blob into IndexedDB —
 *   `UnknownError: Error preparing Blob/File data to be stored in object store`
 * — so on Safari every picture failed to save and the page reported the file as "not an image",
 * which is exactly the wrong diagnosis. Chromium stores it happily, so the whole feature was
 * green here and dead there. An ArrayBuffer clones on every engine.
 */
export async function setAvatar(file: Blob): Promise<void> {
  const small = await downscale(file);
  await setAvatarBytes(await small.arrayBuffer(), small.type);
}

/**
 * The one write, and the only shape the store ever holds.
 *
 * The cropper hands its canvas straight here as bytes and a type rather than as the Blob it
 * produced, so nothing a canvas made ever crosses a structured-clone boundary — see the note
 * above for what that costs on WebKit when it does.
 */
export async function setAvatarBytes(bytes: ArrayBuffer, type: string): Promise<void> {
  const at = Date.now();
  await runOn('readwrite', store =>
    store.put({ id: ROW, bytes, type: type || 'image/png', at } satisfies AvatarRow) as IDBRequest<IDBValidKey>);
  updateProfile({ avatarAt: at });
}

export async function clearAvatar(): Promise<void> {
  await runOn('readwrite', store => store.delete(ROW) as unknown as IDBRequest<undefined>);
  updateProfile({ avatarAt: 0 });
}

export async function loadAvatar(): Promise<Blob | null> {
  if (state.value.avatarAt === 0) return null;
  const row = await runOn<AvatarRow | undefined>('readonly', store => store.get(ROW));
  if (!row) return null;
  // A picture stored before the shape changed is still a Blob on the row.
  if (row.bytes) return new Blob([row.bytes], { type: row.type || 'image/png' });
  return row.blob ?? null;
}

/**
 * An object URL that exists only while something is displaying it.
 *
 * `createObjectURL` pins the blob in memory until it is revoked, so a component that makes one
 * per render and never revokes leaks the whole picture on every navigation. Tied to the calling
 * scope: the URL is revoked when the component unmounts, and again before each replacement.
 */
export function useAvatarUrl() {
  const url = ref<string | null>(null);

  const release = () => {
    if (url.value) URL.revokeObjectURL(url.value);
    url.value = null;
  };

  watch(() => state.value.avatarAt, async at => {
    release();
    if (at === 0) return;
    const blob = await loadAvatar().catch(() => null);
    // The picture may have changed again while this read was in flight; the later write wins.
    if (!blob || state.value.avatarAt !== at) return;
    url.value = URL.createObjectURL(blob);
  }, { immediate: true });

  onScopeDispose(release);
  return url;
}
